import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createOrder } from "@/api/orderApi";

export default function PaymentCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 💡 1. 비회원 주문번호 상태 및 복사 함수 추가
  const [successOrderNo, setSuccessOrderNo] = useState<string | null>(null);

  const handleCopyOrderNo = async () => {
    if (!successOrderNo) return;
    try {
      await navigator.clipboard.writeText(successOrderNo);
      alert("주문번호가 복사되었습니다.");
    } catch (err) {
      alert("복사에 실패했습니다. 화면의 주문번호를 직접 선택하여 복사해주세요.");
    }
  };

  useEffect(() => {
    // 포트원이 리다이렉트 하면서 URL 파라미터로 넘겨준 결과값
    const paymentId = searchParams.get("paymentId");
    const code = searchParams.get("code");
    const message = searchParams.get("message");

    // 1. 에러 코드가 있다면 결제 실패/취소
    if (code != null) {
      alert(`결제에 실패했습니다: ${message}`);
      navigate("/cart");
      return;
    }

    // 2. 세션 스토리지에서 데이터 꺼내기 (OrderPage에서 저장한 구조 그대로 가져옴)
    const tempData = sessionStorage.getItem("ORDER_TEMP_DATA");
    if (!tempData) {
      alert("주문 정보를 찾을 수 없습니다. 다시 시도해주세요.");
      navigate("/");
      return;
    }

    // 💡 3. 파싱 로직 수정 (전체 payload 객체로 바로 사용)
    const orderPayload = JSON.parse(tempData);

    // 4. 백엔드 주문 생성 API 호출
    const processOrder = async () => {
      try {
        const res = await createOrder({
          ...orderPayload, // 저장해둔 모든 데이터 펼치기
          impUid: paymentId,
          merchantUid: paymentId,
        });

        // 성공 시 임시 데이터 비우기
        sessionStorage.removeItem("ORDER_TEMP_DATA");

        // 💡 5. 백엔드 응답에서 생성된 최종 주문번호 추출
        // const finalOrderNo = res?.data?.orderNo || res?.orderNo || paymentId;

        // 💡 5. 백엔드 응답에서 생성된 최종 주문번호 추출 (문자열 자체 추출)
        // const responseData = res?.data?.data || res?.data || res;
        // const finalOrderNo = typeof responseData === 'string' ? responseData : paymentId;
        const finalOrderNo = (res && res.data) ? res.data : paymentId;

        if (orderPayload.isGuest) {
          // 비회원이면 알림 대신 팝업 상태값 변경
          setSuccessOrderNo(finalOrderNo);
        } else {
          // 회원이면 바로 마이페이지로 이동
          alert("주문이 완료되었습니다.");
          navigate("/mypage");
        }

      } catch (error) {
        console.error("결제 콜백 에러:", error);
        alert("결제는 성공했으나, 주문서 생성 중 오류가 발생했습니다. 고객센터로 문의해주세요.");
      }
    };

    // 중복 호출 방지를 위해 successOrderNo가 없을 때만 실행
    if (paymentId && !successOrderNo) {
      processOrder();
    }
  }, [navigate, searchParams, successOrderNo]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 text-slate-500">

      {/* 주문 처리 중일 때 노출되는 화면 */}
      {!successOrderNo ? (
        <p className="text-lg font-medium animate-pulse">결제를 마무리하고 있습니다. 잠시만 기다려주세요...</p>
      ) : (

        /* 💡 6. 비회원 주문 완료 팝업 UI 추가 */
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-[28px] bg-white p-8 shadow-2xl text-center animate-fade-in relative">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 mb-6">
              <svg className="h-8 w-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h2 className="text-2xl font-black text-slate-800 mb-3 tracking-tight">주문이 완료되었습니다!</h2>

            <div className="bg-rose-50 border border-rose-100 text-rose-700 text-sm font-medium py-3 px-4 rounded-xl mb-6 break-keep leading-relaxed">
              비회원 주문 조회를 위해 아래 <strong className="font-bold text-rose-700 underline underline-offset-4">주문번호</strong>를 반드시 메모하거나 복사해 주세요.
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 mb-8 flex flex-col items-center justify-center gap-3">
              <span className="text-sm text-slate-500 font-bold">주문번호</span>
              <span className="text-[22px] font-black tracking-wider text-slate-900">{successOrderNo}</span>

              <button
                onClick={handleCopyOrderNo}
                className="mt-3 flex items-center justify-center gap-1.5 w-full h-11 rounded-xl bg-white border border-slate-300 text-sm font-bold text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-colors shadow-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
                </svg>
                주문번호 복사하기
              </button>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setSuccessOrderNo(null); navigate("/"); }}
                className="flex-1 h-14 rounded-xl border border-slate-300 bg-white text-slate-700 font-bold hover:bg-slate-50 transition-colors"
              >
                홈으로 가기
              </button>
              <button
                onClick={() => { setSuccessOrderNo(null); navigate("/guest/order"); }}
                className="flex-1 h-14 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors shadow-sm"
              >
                주문 내역 확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}